import React, { useState, useEffect } from 'react';

const LLMConfigContent = () => {
  const [config, setConfig] = useState({
    ocrProvider: 'mistral',
    extractionProvider: 'gemini',
    insightsProvider: 'gemini',
    geminiModel: 'gemini-2.5-pro',
    groqModel: 'llama-3.3-70b-versatile',
    geminiApiKey: '',
    groqApiKey: ''
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);

  // Load config from localStorage on mount
  useEffect(() => {
    const savedConfig = localStorage.getItem('llmConfig');
    if (savedConfig) {
      setConfig(JSON.parse(savedConfig));
    }
  }, []);

  const handleProviderChange = (type, value) => {
    setConfig(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleApiKeyChange = (provider, value) => {
    setConfig(prev => ({
      ...prev,
      [`${provider}ApiKey`]: value
    }));
  };

  const handleModelChange = (provider, value) => {
    setConfig(prev => ({
      ...prev,
      [`${provider}Model`]: value
    }));
  };

  const handleSave = () => {
    setIsSaving(true);
    
    // Save to localStorage
    localStorage.setItem('llmConfig', JSON.stringify(config));
    
    // Simulate saving
    setTimeout(() => {
      setIsSaving(false);
      setSaveStatus({
        type: 'success',
        message: 'Konfigurasi LLM berhasil disimpan!'
      });
      
      // Clear status after 3 seconds
      setTimeout(() => setSaveStatus(null), 3000);
    }, 500);
  };

  const handleReset = () => {
    if (window.confirm('Reset konfigurasi ke default?')) {
      const defaultConfig = {
        ocrProvider: 'mistral',
        extractionProvider: 'gemini',
        insightsProvider: 'gemini',
        geminiModel: 'gemini-2.5-pro',
        groqModel: 'llama-3.3-70b-versatile',
        geminiApiKey: '',
        groqApiKey: ''
      };
      setConfig(defaultConfig);
      localStorage.setItem('llmConfig', JSON.stringify(defaultConfig));
      setSaveStatus({
        type: 'success',
        message: 'Konfigurasi direset ke default!'
      });
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const providers = [
    {
      id: 'gemini',
      name: 'Google Gemini',
      icon: 'fa-google',
      description: 'Google Gemini - High accuracy',
      color: 'blue',
      models: [
        { id: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro', description: 'Most capable, best for complex tasks' },
        { id: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash', description: 'Fast & balanced' },
        { id: 'gemini-2.0-flash', name: 'Gemini 2.0 Flash', description: 'Fast generation' },
        { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro', description: 'Previous gen, stable' },
        { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash', description: 'Fastest, cost-effective' }
      ]
    },
    {
      id: 'groq',
      name: 'Groq (Llama)',
      icon: 'fa-bolt',
      description: 'Meta Llama models via Groq - Fastest inference',
      color: 'purple',
      models: [
        { id: 'llama-3.3-70b-versatile', name: 'Llama 3.3 70B Versatile', description: 'Latest, most capable' },
        { id: 'llama-3.2-90b-vision-preview', name: 'Llama 3.2 90B Vision', description: 'Multimodal with vision' },
        { id: 'llama-3.1-70b-versatile', name: 'Llama 3.1 70B Versatile', description: 'Balanced performance' },
        { id: 'llama-3.1-8b-instant', name: 'Llama 3.1 8B Instant', description: 'Fastest, lightweight' }
      ]
    }
  ];

  const getProviderColor = (providerId) => {
    const provider = providers.find(p => p.id === providerId);
    return provider?.color || 'gray';
  };

  return (
    <main className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center transition-colors duration-300">
          <i className="fa-solid fa-sliders mr-3 text-indigo-600 dark:text-indigo-400"></i>
          Konfigurasi LLM
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mt-2 transition-colors duration-300">
          Pilih Large Language Model yang akan digunakan untuk ekstraksi data dan analisis
        </p>
      </div>

      {/* Status Message */}
      {saveStatus && (
        <div className={`mb-6 p-4 rounded-lg transition-colors duration-300 ${
          saveStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
        }`}>
          <div className={`flex items-center transition-colors duration-300 ${
            saveStatus.type === 'success' ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'
          }`}>
            <i className={`fa-solid ${
              saveStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
            } mr-2`}></i>
            <span>{saveStatus.message}</span>
          </div>
        </div>
      )}

      {/* OCR Provider */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
        <div className="flex items-center mb-4">
          <i className="fa-solid fa-file-pdf text-red-600 text-2xl mr-3"></i>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white transition-colors duration-300">OCR Provider</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">Model untuk ekstraksi text dari PDF</p>
          </div>
        </div>
        
        <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg transition-colors duration-300">
          <div className="flex items-center">
            <i className="fa-solid fa-lock text-gray-400 dark:text-gray-500 mr-2"></i>
            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium transition-colors duration-300">Mistral OCR (Fixed)</span>
            <span className="ml-auto px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-full transition-colors duration-300">
              Active
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 transition-colors duration-300">
            OCR provider saat ini menggunakan Mistral AI dan tidak dapat diubah
          </p>
        </div>
      </div>

      {/* Data Extraction Provider */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
        <div className="flex items-center mb-4">
          <i className="fa-solid fa-brain text-indigo-600 text-2xl mr-3"></i>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white transition-colors duration-300">Data Extraction LLM</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">Model untuk ekstraksi data keuangan dari PDF</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {providers.map(provider => (
            <div
              key={provider.id}
              onClick={() => handleProviderChange('extractionProvider', provider.id)}
              className={`cursor-pointer p-4 border-2 rounded-lg transition-all ${
                config.extractionProvider === provider.id
                  ? provider.color === 'blue' 
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <i className={`fa-brands ${provider.icon} text-xl mr-2 ${provider.color === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`}></i>
                  <span className="font-semibold text-gray-800 dark:text-white transition-colors duration-300">{provider.name}</span>
                </div>
                {config.extractionProvider === provider.id && (
                  <i className={`fa-solid fa-check-circle ${provider.color === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`}></i>
                )}
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 transition-colors duration-300">{provider.description}</p>
            </div>
          ))}
        </div>

        {/* Model Selection for Extraction */}
        {config.extractionProvider && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
              Model untuk Data Extraction
            </label>
            <select
              value={config[`${config.extractionProvider}Model`]}
              onChange={(e) => handleModelChange(config.extractionProvider, e.target.value)}
              className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-300"
            >
              {providers.find(p => p.id === config.extractionProvider)?.models.map(model => (
                <option key={model.id} value={model.id}>
                  {model.name} - {model.description}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* AI Insights Provider */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
        <div className="flex items-center mb-4">
          <i className="fa-solid fa-lightbulb text-yellow-600 text-2xl mr-3"></i>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white transition-colors duration-300">AI Insights LLM</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">Model untuk generate financial insights & recommendations</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {providers.map(provider => (
            <div
              key={provider.id}
              onClick={() => handleProviderChange('insightsProvider', provider.id)}
              className={`cursor-pointer p-4 border-2 rounded-lg transition-all ${
                config.insightsProvider === provider.id
                  ? provider.color === 'blue' 
                    ? 'border-blue-500 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/30'
                    : 'border-purple-500 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 bg-white dark:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <i className={`fa-brands ${provider.icon} text-xl mr-2 ${provider.color === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`}></i>
                  <span className="font-semibold text-gray-800 dark:text-white transition-colors duration-300">{provider.name}</span>
                </div>
                {config.insightsProvider === provider.id && (
                  <i className={`fa-solid fa-check-circle ${provider.color === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`}></i>
                )}
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-300 transition-colors duration-300">{provider.description}</p>
            </div>
          ))}
        </div>

        {/* Model Selection for Insights */}
        {config.insightsProvider && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
              Model untuk AI Insights
            </label>
            <select
              value={config[`${config.insightsProvider}Model`]}
              onChange={(e) => handleModelChange(config.insightsProvider, e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              {providers.find(p => p.id === config.insightsProvider)?.models.map(model => (
                <option key={model.id} value={model.id}>
                  {model.name} - {model.description}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* API Keys */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
        <div className="flex items-center mb-4">
          <i className="fa-solid fa-key text-green-600 text-2xl mr-3"></i>
          <div>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white transition-colors duration-300">API Keys</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">Masukkan API keys untuk provider yang dipilih</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Gemini API Key */}
          {(config.extractionProvider === 'gemini' || config.insightsProvider === 'gemini') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                <i className="fa-brands fa-google mr-2 text-blue-600"></i>
                Google Gemini API Key
              </label>
              <input
                type="password"
                value={config.geminiApiKey}
                onChange={(e) => handleApiKeyChange('gemini', e.target.value)}
                placeholder="AIza..."
                className="w-full px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-300"
              />
              <p className="text-xs text-gray-500 mt-1">
                Kosongkan untuk menggunakan API key default dari server
              </p>
            </div>
          )}

          {/* Groq API Key */}
          {(config.extractionProvider === 'groq' || config.insightsProvider === 'groq') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                <i className="fa-solid fa-bolt mr-2 text-purple-600"></i>
                Groq Cloud API Key
              </label>
              <input
                type="password"
                value={config.groqApiKey}
                onChange={(e) => handleApiKeyChange('groq', e.target.value)}
                placeholder="gsk-..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Dapatkan API key dari <a href="https://console.groq.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">console.groq.com</a>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center space-x-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center ${
            isSaving
              ? 'bg-gray-400 text-white cursor-not-allowed'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white'
          }`}
        >
          <i className={`fa-solid ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'} mr-2`}></i>
          {isSaving ? 'Menyimpan...' : 'Simpan Konfigurasi'}
        </button>
        
        <button
          onClick={handleReset}
          className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-300"
        >
          <i className="fa-solid fa-rotate-left mr-2"></i>
          Reset
        </button>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors duration-300">
        <div className="flex items-start">
          <i className="fa-solid fa-info-circle text-blue-600 dark:text-blue-400 mr-3 mt-1 transition-colors duration-300"></i>
          <div className="text-sm text-blue-800 dark:text-blue-200 transition-colors duration-300">
            <p className="font-medium mb-2">Informasi Penting:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Konfigurasi disimpan di browser (localStorage)</li>
              <li>API key disimpan secara lokal, tidak dikirim ke server</li>
              <li>Jika API key kosong, sistem akan menggunakan API key default dari server</li>
              <li>Perubahan provider akan diterapkan pada upload PDF berikutnya</li>
              <li>Gemini: Akurasi tinggi, response lebih lambat</li>
              <li>Groq (Llama): Response super cepat, cost-effective</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Current Configuration Summary */}
      <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 transition-colors duration-300">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
          <i className="fa-solid fa-list-check mr-2 text-indigo-600 dark:text-indigo-400"></i>
          Konfigurasi Aktif
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded transition-colors duration-300">
            <span className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">OCR Provider</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white transition-colors duration-300">Mistral OCR</span>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded transition-colors duration-300">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">Data Extraction</span>
              <span className={`text-sm font-medium transition-colors duration-300 ${getProviderColor(config.extractionProvider) === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`}>
                {providers.find(p => p.id === config.extractionProvider)?.name}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
              Model: {providers.find(p => p.id === config.extractionProvider)?.models.find(m => m.id === config[`${config.extractionProvider}Model`])?.name}
            </div>
          </div>
          <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded transition-colors duration-300">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">AI Insights</span>
              <span className={`text-sm font-medium transition-colors duration-300 ${getProviderColor(config.insightsProvider) === 'blue' ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'}`}>
                {providers.find(p => p.id === config.insightsProvider)?.name}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
              Model: {providers.find(p => p.id === config.insightsProvider)?.models.find(m => m.id === config[`${config.insightsProvider}Model`])?.name}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default LLMConfigContent;

